import React, { useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import {
  CAMERA_GROUPS, getModelsForSetup, calculateCores, getStorage,
  getNearestSSDSize, selectHardware, buildSummary, buildShortSummary,
} from '../utils/aiCalc';
import './AIAnalytics.css';

const emptyCounts = CAMERA_GROUPS.reduce((acc, g) => ({ ...acc, [g.id]: '' }), {});
const AUTO = '';
const VERSIONS = [
  {
    label: 'version-2',
    date: '19-10-2026',
    notes: [
      'Removed the Total FPS block from the server configuration summary.',
      'Fixed the GPU bug: the GPU block now shows only when GPU is enabled in general settings.',
    ],
  },
  {
    label: 'version-1',
    date: '14-10-2026',
    notes: [
      'Simplified UI: one camera count per feature group, live results.',
      'Total FPS and vCPU/storage breakdown moved to calculation details.',
      'Calculation formulas unchanged.',
    ],
  },
];
const APP_VERSION = VERSIONS[0];

function AIAnalytics() {
  const [counts, setCounts] = useState(emptyCounts);
  const [enableGPU, setEnableGPU] = useState(false);
  const [retentionMonths, setRetentionMonths] = useState(3);
  const [bufferPercentage, setBufferPercentage] = useState(0);
  const [splitStorage, setSplitStorage] = useState(false);
  const [overrides, setOverrides] = useState({ setupType: AUTO, modelId: AUTO, slaveCount: AUTO, processorType: AUTO });
  const [calculated, setCalculated] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [toast, setToast] = useState('');
  const [showVersionInfo, setShowVersionInfo] = useState(false);
  const [customerName, setCustomerName] = useState('');

  const cores = useMemo(() => calculateCores(counts, enableGPU), [counts, enableGPU]);
  const hasInput = cores.totalCameras > 0;

  const baseVCPU = cores.totalVCPU;
  const bufferVCPU = Math.ceil((baseVCPU * bufferPercentage) / 100);
  const requiredVCPU = baseVCPU + bufferVCPU;

  const hw = useMemo(() => selectHardware(requiredVCPU, overrides), [requiredVCPU, overrides]);
  const storage = useMemo(() => getStorage(cores.totalCameras, retentionMonths), [cores.totalCameras, retentionMonths]);
  const ssd = getNearestSSDSize(storage.totalWithBufferGB);

  const setOverride = (patch) => setOverrides((o) => ({ ...o, ...patch }));
  // Changing setup type resets the dependent manual picks back to auto
  const changeSetupType = (v) => setOverrides({ setupType: v, modelId: AUTO, slaveCount: AUTO, processorType: AUTO });

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2000);
  };

  const copy = (text, msg) => {
    const fallback = () => {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      showToast(msg);
    };
    if (navigator.clipboard) navigator.clipboard.writeText(text).then(() => showToast(msg)).catch(fallback);
    else fallback();
  };

  const ctx = { cores, hw, requiredVCPU, baseVCPU, storage, splitStorage, enableGPU, bufferPercentage };

  const getExportData = () => {
    const now = new Date();
    const pad = (v) => String(v).padStart(2, '0');
    const dateStr = `${pad(now.getDate())}-${pad(now.getMonth() + 1)}-${now.getFullYear()}`;
    const customer = customerName.trim();
    const slug = customer.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
    const fileBase = `${slug || 'deepsight'}_specs_${dateStr}`;
    const customerLabel = customer.replace(/(^|\s)(\S)/g, (m, sp, ch) => sp + ch.toUpperCase());

    const featureRows = CAMERA_GROUPS
      .map((g) => [g.name, Math.trunc(Number(counts[g.id]) || 0)])
      .filter(([, c]) => c > 0);
    const specRows = buildShortSummary(ctx).split('\n')
      .map((l) => l.trim())
      .filter((l) => l && l !== 'Server Specs:')
      .map((l) => {
        const m = l.match(/^(.+?)(?::| -)\s+(.+)$/);
        return m ? [m[1], m[2]] : [l, ''];
      });

    const isNUC = hw.model.type === 'nuc';
    const processor = hw.setupType === 'dual' || (hw.setupType === 'multi' && hw.processorType === 'dual') ? 'Dual' : 'Single';
    const serverRows = [
      ['Setup Type', { single: isNUC ? 'NUC Box' : 'Single Server', dual: 'Single Machine - Dual Processor', multi: 'Multi-Server Setup' }[hw.setupType]],
      ['Server Name', hw.model.name],
      ...(isNUC ? [] : [['Processor', processor]]),
      ...(hw.setupType === 'multi' ? [['Slave Nodes', hw.slaveCount]] : []),
    ];
    return { dateStr, fileBase, customerLabel, featureRows, serverRows: [...serverRows, ...specRows] };
  };

  const exportExcel = () => {
    const { dateStr, fileBase, customerLabel, featureRows, serverRows } = getExportData();
    const rows = [
      ['Customer Info'],
      ...(customerLabel ? [['Customer / Project', customerLabel]] : []),
      ['Date', dateStr],
      [],
      ['Camera Summary'],
      ['Total Cameras', cores.totalCameras],
      ...featureRows,
      [],
      ['Server Specs'],
      ...serverRows,
    ];
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [{ wch: 40 }, { wch: 30 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Summary Specs');
    XLSX.writeFile(wb, `${fileBase}.xlsx`);
  };

  const exportPdf = () => {
    const prevTitle = document.title;
    document.title = getExportData().fileBase; // browser uses the title as the default PDF file name
    const restore = () => { document.title = prevTitle; window.removeEventListener('afterprint', restore); };
    window.addEventListener('afterprint', restore);
    window.print();
  };

  const mult = hw.processorType === 'dual' ? 2 : 1;
  const ramGB =
    hw.setupType === 'single' ? (hw.model.type === 'nuc' ? hw.model.ram : hw.model.vcpu * 2)
      : hw.setupType === 'dual' ? hw.model.vcpu * 4
        : 16 + hw.slaveCount * hw.model.vcpu * 2 * mult;

  const gpuText = enableGPU && (cores.gpuL4 > 0 || cores.gpu4090 > 0)
    ? [cores.gpuL4 > 0 && `${cores.gpuL4} × L4`, cores.gpu4090 > 0 && `${cores.gpu4090} × 4090`].filter(Boolean).join(', ')
    : 'None';
  const showResult = calculated && hasInput;

  const SummaryCard = ({ label, value, small }) => (
    <div className="ai-sum-card">
      <div className="ai-sum-label">{label}</div>
      <div className="ai-sum-value" style={{ fontSize: small ? '20px' : '30px' }}>{value}</div>
    </div>
  );

  return (
    <>
    <div className="ai-calc vms-container">
      {/* Header */}
      <div className="ai-header">
        <h1>DeepSight AI Analytics Configuration Calculator</h1>
        <div className="ai-version">
          <button type="button" onClick={() => setShowVersionInfo((v) => !v)}>{APP_VERSION.label}</button>
          {showVersionInfo && (
            <div className="ai-version-pop">
              {VERSIONS.map((v) => (
                <div key={v.label} className="ai-version-entry">
                  <div className="ai-version-date">{v.label} · {v.date}</div>
                  <ul>{v.notes.map((n) => <li key={n}>{n}</li>)}</ul>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="vms-customer-info">
        <div className="vms-section-header">Customer Info</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label className="vms-customer-label" htmlFor="ai-customer">Customer / Project name — optional, used in Excel file name</label>
          <input
            id="ai-customer"
            type="text"
            className="vms-customer-input"
            placeholder="Customer / project name"
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
          />
        </div>
      </div>

      <div className="vms-section-header ai-section-row">
        <span>General Settings</span>
        <button type="button" className="ai-rules-btn" onClick={() => setShowRules((v) => !v)}>
          {showRules ? 'Hide rules ▴' : 'Static rules & limits ⓘ'}
        </button>
      </div>

      {showRules && (
        <div className="ai-rules">
          <div><strong>Backend cores:</strong> 1 core per 2.5 cameras (decoding/pre-processing)</div>
          <div><strong>Inference cores:</strong> 1 core per 20 FPS (AI model processing)</div>
          <div><strong>Cameras per backend container:</strong> 5 cameras</div>
          <div><strong>FPS per inference container:</strong> 20 FPS</div>
          <div><strong>L4 GPU capacity:</strong> 750 detection cameras</div>
          <div><strong>RTX 4090 GPU capacity:</strong> 40 FR cameras</div>
          <div><strong>Base Kubernetes overhead:</strong> 3 vCPU</div>
        </div>
      )}

      {/* Camera counts card */}
      <div className="vms-groups-list">
        <div className="vms-group-card">
          <div className="vms-group-sidebar">
            <span className="vms-group-title">Camera count</span>
            <div style={{ width: '100%' }}>
              <label className="vms-group-streams-label">Total cameras</label>
              <div className="ai-total">{cores.totalCameras}</div>
            </div>
            <label className="vms-checkbox-label">
              <input type="checkbox" className="vms-checkbox" checked={enableGPU} onChange={(e) => setEnableGPU(e.target.checked)} />
              Enable GPU
            </label>
          </div>

          <div className="vms-group-main">
            <div className="vms-group-subsection-title">
              Feature groups
              <span className="vms-info-icon" title="Enter the number of cameras running each feature group">i</span>
            </div>
            <div className="vms-field-grid">
              {CAMERA_GROUPS.map((g) => (
                <div className="vms-field-item" key={g.id}>
                  <label className="vms-field-label" htmlFor={`cam-${g.id}`}>
                    {g.name} <span className="ai-fps">{g.fps} FPS</span>
                    {g.examples && <span className="vms-info-icon" title={g.examples}>i</span>}
                  </label>
                  <input
                    id={`cam-${g.id}`}
                    type="number"
                    min="0"
                    placeholder="0"
                    className="vms-field-input"
                    value={counts[g.id]}
                    onChange={(e) => setCounts({ ...counts, [g.id]: e.target.value })}
                  />
                  {g.examples && <span className="ai-examples">({g.examples})</span>}
                </div>
              ))}
            </div>
            <div className="vms-substreams-section">
              <span className="vms-substreams-label">GPU</span>
              <span className="ai-hint-inline">
                When enabled, inference runs on GPU (L4 ≈ 750 detection cams, RTX 4090 ≈ 40 FR cams). Decoding stays on CPU.
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Sliders */}
      <div className="vms-global-settings-grid" style={{ marginTop: '10px' }}>
        <div className="vms-global-settings-card">
          <div className="vms-slider-container">
            <div className="vms-slider-header"><span>Buffer (vCPU)</span><span>{bufferPercentage}%</span></div>
            <input type="range" className="vms-slider" min="0" max="50" value={bufferPercentage}
              onChange={(e) => setBufferPercentage(Number(e.target.value))} />
          </div>
        </div>
        <div className="vms-global-settings-card">
          <div className="vms-slider-container">
            <div className="vms-slider-header"><span>Alert Storage Retention</span><span>{retentionMonths} months</span></div>
            <span className="vms-slider-desc">Alert images are kept for this many months</span>
            <input type="range" className="vms-slider" min="1" max="24" value={retentionMonths}
              onChange={(e) => setRetentionMonths(Number(e.target.value))} />
          </div>
        </div>
      </div>

      <button className="vms-calculate-btn" onClick={() => setCalculated(true)}>
        Calculate Server Specs
      </button>
      {calculated && !hasInput && <p className="ai-empty">Enter at least one camera count above.</p>}

      {/* Output */}
      {showResult && (
        <div className="ai-output">
          <h2>DeepSight AI Analytics Server Configuration</h2>

          <div className="ai-sum-grid">
            <SummaryCard label="Cameras" value={cores.totalCameras} />
            <SummaryCard label="vCPU Required" value={requiredVCPU} />
            <SummaryCard label="RAM Required" value={`${ramGB}GB`} />
            <SummaryCard label="Total Storage" value={ssd} small />
            {enableGPU && <SummaryCard label="GPU" value={gpuText} small />}
          </div>

          <div className="ai-two">
            <div className="ai-select-card">
              <label>Setup Type</label>
              <select value={overrides.setupType} onChange={(e) => changeSetupType(e.target.value)}>
                <option value={AUTO}>Auto (recommended)</option>
                <option value="single">Single Server</option>
                <option value="dual">Dual Server</option>
                <option value="multi">Multi-Server</option>
              </select>
            </div>
            <div className="ai-select-card">
              <label>Server Model</label>
              <select value={overrides.modelId} onChange={(e) => setOverride({ modelId: e.target.value })}>
                <option value={AUTO}>Auto</option>
                {getModelsForSetup(hw.setupType).map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name} ({hw.setupType === 'dual' ? m.vcpu * 2 : m.vcpu}vCPU)
                  </option>
                ))}
              </select>
            </div>
            {hw.setupType === 'multi' && (
              <>
                <div className="ai-select-card">
                  <label>Slave nodes</label>
                  <input type="number" min="1" value={overrides.slaveCount || hw.slaveCount}
                    onChange={(e) => setOverride({ slaveCount: Math.max(1, Number(e.target.value) || 1) })} />
                </div>
                <div className="ai-select-card">
                  <label>Processor per slave</label>
                  <select value={hw.processorType} onChange={(e) => setOverride({ processorType: e.target.value })}>
                    <option value="single">Single Processor</option>
                    <option value="dual">Dual Processor</option>
                  </select>
                </div>
              </>
            )}
          </div>

          <div className="ai-split">
            <input type="checkbox" id="aiSplitStorage" checked={splitStorage} onChange={(e) => setSplitStorage(e.target.checked)} />
            <label htmlFor="aiSplitStorage">Split Storage (SSD for OS + Docker 500GB, HDD for logs + images)</label>
          </div>

          {!hw.compatible && (
            <div className="ai-warn">
              Not compatible: needs {requiredVCPU} vCPU, selected setup gives {hw.effectiveVCPU} (deficit {requiredVCPU - hw.effectiveVCPU}).
              Choose a bigger model or Dual/Multi-Server.
            </div>
          )}

          <div className="ai-two">
            <div className="ai-panel">
              <div className="ai-panel-head">
                <h3>Detailed Config</h3>
                <div><button onClick={() => copy(buildSummary(ctx), 'Detailed config copied')}>Copy</button></div>
              </div>
              <pre>{buildSummary(ctx)}</pre>
            </div>
            <div className="ai-panel">
              <div className="ai-panel-head">
                <h3>Summary Specs</h3>
                <div>
                  <button onClick={() => copy(buildShortSummary(ctx), 'Summary copied')}>Copy</button>
                  <button onClick={exportExcel}>Excel</button>
                  <button onClick={exportPdf}>Export PDF</button>
                </div>
              </div>
              <pre>{buildShortSummary(ctx)}</pre>
            </div>
          </div>

          <div className="ai-export">
            <button className="ai-export-btn ai-export-pdf" onClick={exportPdf}>Export PDF Report</button>
            <button className="ai-export-btn" onClick={exportExcel}>Export Excel Specs</button>
          </div>

          <div className="ai-details-card">
            <h3>Calculation Details</h3>
            <Details cores={cores} storage={storage} retentionMonths={retentionMonths}
              bufferPercentage={bufferPercentage} bufferVCPU={bufferVCPU} requiredVCPU={requiredVCPU} />
          </div>
        </div>
      )}
      {toast && <div className="ai-toast">{toast}</div>}
    </div>

    {showResult && <PrintReport data={getExportData()} totalCameras={cores.totalCameras} gpuText={gpuText} detail={buildSummary(ctx)} />}
    </>
  );
}

// Hidden on screen; App.css @media print shows it in place of the calculator
function PrintReport({ data, totalCameras, gpuText, detail }) {
  const h2 = { fontSize: '18px', color: '#111827', borderBottom: '1px solid #e2e8f0', paddingBottom: '8px', marginBottom: '15px' };
  const cell = { padding: '8px', textAlign: 'left' };
  const table = (rows) => (
    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '25px', fontSize: '13px' }}>
      <tbody>
        {rows.map(([k, v]) => (
          <tr key={k} style={{ borderBottom: '1px solid #e2e8f0' }}>
            <td style={{ ...cell, width: '45%' }}><strong>{k}</strong></td>
            <td style={cell}>{v}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
  return (
    <div className="vms-print-report" style={{ padding: '20px', fontFamily: 'sans-serif', color: '#1e293b' }}>
      <div style={{ borderBottom: '2px solid #1a6fdb', paddingBottom: '15px', marginBottom: '25px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ color: '#111827', margin: 0, fontSize: '24px' }}>DeepSight AI Analytics Specification Report</h1>
        <span style={{ fontSize: '12px', color: '#64748b' }}>Date: {data.dateStr}</span>
      </div>
      {data.customerLabel && (
        <div style={{ marginBottom: '20px', background: '#f8fafc', padding: '15px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
          <strong style={{ color: '#111827' }}>Customer / Project:</strong> {data.customerLabel}
        </div>
      )}
      <h2 style={h2}>Camera Summary</h2>
      {table([['Total Cameras', totalCameras], ...data.featureRows])}
      <h2 style={h2}>Server Specifications</h2>
      {table(gpuText !== 'None' ? [...data.serverRows, ['GPU', gpuText]] : data.serverRows)}
      <h2 style={h2}>Detailed Specification Output</h2>
      <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '11px', background: '#fafbfc', padding: '15px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>{detail}</pre>
    </div>
  );
}

function Row({ label, value }) {
  return <tr><td>{label}</td><td className="num">{value}</td></tr>;
}

function Details({ cores: c, storage, retentionMonths, bufferPercentage, bufferVCPU, requiredVCPU }) {
  return (
    <div className="ai-details">
      <h4>Total FPS</h4>
      <table>
        <tbody>
          <Row label="Total FPS (all cameras × FPS)" value={c.totalFPS.toFixed(1)} />
          <Row label="Inference containers (total FPS ÷ 20)" value={Math.ceil(c.totalFPS / 20)} />
          <Row label="Backend containers (cameras ÷ 5)" value={Math.ceil(c.totalCameras / 5)} />
        </tbody>
      </table>

      <h4>vCPU breakdown</h4>
      <table>
        <tbody>
          <Row label="Detection backend (cameras ÷ 2.5)" value={c.detectionBackendVcore} />
          <Row label={`Detection inference (${c.detectionFPS.toFixed(1)} FPS ÷ 20)`} value={`${c.detectionInferencingVcore}${c.detectionInferencingVcore !== c.detectionInferencingVcoreBase ? ` (CPU: ${c.detectionInferencingVcoreBase})` : ''}`} />
          <Row label="Tailgating (4 per camera)" value={c.tailgatingVcore} />
          <Row label="ALPR (1 per camera)" value={c.alprVcore} />
          <Row label="Camera tamper (2 per 5 cameras)" value={c.tamperVcore} />
          <Row label="Invigilator (1 per camera)" value={c.invigilatorVcore} />
          <Row label="Speed (2 per camera)" value={c.speedVcore} />
          <Row label="FR backend" value={c.frBackendVcore} />
          <Row label="FR inference" value={`${c.frInferencingVcore}${c.frInferencingVcore !== c.frInferencingVcoreBase ? ` (CPU: ${c.frInferencingVcoreBase})` : ''}`} />
          <Row label="Entry-exit (2 per camera)" value={c.entryExitVcore} />
          <Row label="Kubernetes overhead" value={c.k8sVcore} />
          <Row label="Total vCPU (current)" value={c.totalVCPU} />
          <Row label="Total vCPU if everything on CPU" value={c.cpuOnlyBaseVCPU} />
          {bufferPercentage > 0 && <Row label={`Buffer ${bufferPercentage}%`} value={`+${bufferVCPU} → ${requiredVCPU}`} />}
        </tbody>
      </table>

      <h4>Storage</h4>
      <table>
        <tbody>
          <Row label={`Alerts per day (${c.totalCameras} cameras × 250KB each)`} value={storage.alertsPerDay} />
          <Row label="Storage per day (alerts)" value={`${storage.gbPerDay.toFixed(3)} GB`} />
          <Row label={`Alerts for ${retentionMonths} months`} value={`${storage.imageGB.toFixed(2)} GB`} />
          <Row label="OS / Docker / app base" value={`${storage.baseSystemGB} GB`} />
          <Row label="Total before buffer" value={`${storage.totalWithoutBuffer.toFixed(2)} GB`} />
          <Row label="Total with 30% buffer" value={`${storage.totalWithBufferGB.toFixed(2)} GB`} />
        </tbody>
      </table>

    </div>
  );
}

export default AIAnalytics;
